import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { useMutation, useQuery } from '@apollo/client/react';
import classNames from 'classnames';
import { PenLine } from 'lucide-react';
import log from 'loglevel';

import { useToast } from '@admin-platform/ui/components';

import { useTenantFeatures } from '../../../hooks/useTenantFeatures';
import { TenantFeature } from '../../../types/tenant.types';
import { parseGraphQLErrorForUser } from '../../../utils/normalizeGraphqlError';
import { Alert, AlertDescription } from '../../Alert/Alert';
import { Badge } from '../../Badge/Badge';
import { Button } from '../../Button/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../Card/Card';
import ConfirmationModal from '../../ConfirmationModal/ConfirmationModal';
import { Input } from '../../Inputs/Input';
import { Label } from '../../Inputs/Label';
import { Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle } from '../../Modal/Modal';
import { SignatureEditor } from '../../SignatureEditor/SignatureEditor';

import GMAIL_INTEGRATION from '../GmailSettings/queries/gmailIntegration.query.graphql';
import type {
  GmailIntegrationQuery,
  GmailIntegrationQueryVariables,
} from '../GmailSettings/queries/gmailIntegration.query.graphql';

import MY_EMAIL_SIGNATURES from './queries/myEmailSignatures.query.graphql';
import type {
  MyEmailSignaturesQuery,
  MyEmailSignaturesQueryVariables,
} from './queries/myEmailSignatures.query.graphql';
import CREATE_EMAIL_SIGNATURE from './mutations/createEmailSignature.mutation.graphql';
import type {
  CreateEmailSignatureMutation,
  CreateEmailSignatureMutationVariables,
} from './mutations/createEmailSignature.mutation.graphql';
import UPDATE_EMAIL_SIGNATURE from './mutations/updateEmailSignature.mutation.graphql';
import type {
  UpdateEmailSignatureMutation,
  UpdateEmailSignatureMutationVariables,
} from './mutations/updateEmailSignature.mutation.graphql';
import SET_DEFAULT_EMAIL_SIGNATURE from './mutations/setDefaultEmailSignature.mutation.graphql';
import type {
  SetDefaultEmailSignatureMutation,
  SetDefaultEmailSignatureMutationVariables,
} from './mutations/setDefaultEmailSignature.mutation.graphql';
import DELETE_EMAIL_SIGNATURE from './mutations/deleteEmailSignature.mutation.graphql';
import type {
  DeleteEmailSignatureMutation,
  DeleteEmailSignatureMutationVariables,
} from './mutations/deleteEmailSignature.mutation.graphql';
import IMPORT_GMAIL_SIGNATURES from './mutations/importGmailSignatures.mutation.graphql';
import type {
  ImportGmailSignaturesMutation,
  ImportGmailSignaturesMutationVariables,
} from './mutations/importGmailSignatures.mutation.graphql';

import styles from './EmailSignatures.module.css';

type Signature = MyEmailSignaturesQuery['myEmailSignatures'][number];

interface Draft {
  id?: string;
  name: string;
  sendingAddress: string;
  bodyHtml: string;
}

const EMPTY_DRAFT: Draft = { name: '', sendingAddress: '', bodyHtml: '' };

/**
 * Per-operator email signatures. Sits with Gmail on Account rather than under
 * tenant Settings for the same reason the mailbox does: a signature is how one
 * person signs off, not a tenant-wide setting.
 */
export default function EmailSignatures(): JSX.Element {
  const toast = useToast();
  const { hasFeature } = useTenantFeatures();
  const crmEnabled = hasFeature(TenantFeature.CRM);

  const [draft, setDraft] = useState<Draft | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Signature | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const { data, loading, error, refetch } = useQuery<
    MyEmailSignaturesQuery,
    MyEmailSignaturesQueryVariables
  >(MY_EMAIL_SIGNATURES, { fetchPolicy: 'cache-and-network' });

  const { data: gmailData } = useQuery<GmailIntegrationQuery, GmailIntegrationQueryVariables>(
    GMAIL_INTEGRATION,
    { skip: !crmEnabled },
  );
  const gmailConnected = crmEnabled && (gmailData?.gmailIntegration?.connected ?? false);

  const [createSignature, { loading: creating }] = useMutation<
    CreateEmailSignatureMutation,
    CreateEmailSignatureMutationVariables
  >(CREATE_EMAIL_SIGNATURE);
  const [updateSignature, { loading: updating }] = useMutation<
    UpdateEmailSignatureMutation,
    UpdateEmailSignatureMutationVariables
  >(UPDATE_EMAIL_SIGNATURE);
  const [setDefaultSignature] = useMutation<
    SetDefaultEmailSignatureMutation,
    SetDefaultEmailSignatureMutationVariables
  >(SET_DEFAULT_EMAIL_SIGNATURE);
  const [deleteSignature, { loading: deleting }] = useMutation<
    DeleteEmailSignatureMutation,
    DeleteEmailSignatureMutationVariables
  >(DELETE_EMAIL_SIGNATURE);
  const [importFromGmail, { loading: importing }] = useMutation<
    ImportGmailSignaturesMutation,
    ImportGmailSignaturesMutationVariables
  >(IMPORT_GMAIL_SIGNATURES);

  const signatures = data?.myEmailSignatures ?? [];

  // A stale form error would still be showing after the operator fixes the
  // field and reopens the dialog.
  useEffect(() => {
    if (draft === null) setFormError(null);
  }, [draft]);

  /**
   * Refresh the list after a write that has already succeeded.
   *
   * Deliberately swallows its own failure. The mutation is committed by the
   * time this runs, so letting a refetch error reach the caller's catch would
   * report a saved signature as "failed to save" — and a retry of a *create*
   * makes a second signature, because only the Gmail import is an
   * address-keyed upsert. A stale list is the lesser problem and corrects
   * itself on the next render.
   */
  const refreshList = useCallback(async () => {
    try {
      await refetch();
    } catch (err) {
      log.warn('Signature list refresh failed after a successful write', err);
    }
  }, [refetch]);

  const handleSave = useCallback(async () => {
    if (!draft) return;
    if (!draft.name.trim()) {
      setFormError('Give the signature a name so you can pick it when sending.');
      return;
    }
    if (!draft.bodyHtml.trim()) {
      setFormError('The signature is empty.');
      return;
    }
    setFormError(null);

    try {
      if (draft.id) {
        await updateSignature({
          variables: {
            input: {
              id: draft.id,
              name: draft.name.trim(),
              bodyHtml: draft.bodyHtml,
              // Null rather than omitted: an operator clearing the field means
              // "this signature is no longer tied to one address".
              sendingAddress: draft.sendingAddress.trim() || null,
            },
          },
        });
      } else {
        await createSignature({
          variables: {
            input: {
              name: draft.name.trim(),
              bodyHtml: draft.bodyHtml,
              sendingAddress: draft.sendingAddress.trim() || undefined,
            },
          },
        });
      }
      // Everything below this point is post-success: the draft closes and the
      // operator is told it saved before the refresh is attempted.
      setDraft(null);
      toast('Signature saved', 'success');
    } catch (err) {
      log.error('Failed to save signature', err);
      setFormError(parseGraphQLErrorForUser(err));
      return;
    }
    await refreshList();
  }, [createSignature, draft, refreshList, toast, updateSignature]);

  const handleMakeDefault = useCallback(
    async (signature: Signature) => {
      try {
        await setDefaultSignature({ variables: { id: signature.id } });
      } catch (err) {
        log.error('Failed to set default signature', err);
        toast(parseGraphQLErrorForUser(err), 'error');
        return;
      }
      toast(`"${signature.name}" is now your default signature`, 'success');
      await refreshList();
    },
    [refreshList, setDefaultSignature, toast],
  );

  const handleDelete = useCallback(async () => {
    if (!pendingDelete) return;
    try {
      await deleteSignature({ variables: { id: pendingDelete.id } });
    } catch (err) {
      log.error('Failed to delete signature', err);
      toast(parseGraphQLErrorForUser(err), 'error');
      return;
    } finally {
      setPendingDelete(null);
    }
    toast('Signature deleted', 'success');
    await refreshList();
  }, [deleteSignature, pendingDelete, refreshList, toast]);

  const handleImport = useCallback(async () => {
    let imported: unknown[] = [];
    try {
      const result = await importFromGmail({ variables: {} });
      imported = result.data?.importGmailSignatures ?? [];
    } catch (err) {
      log.error('Failed to import Gmail signatures', err);
      toast(parseGraphQLErrorForUser(err), 'error');
      return;
    }
    toast(
      imported.length > 0
        ? 'Imported your Gmail signature'
        : 'Gmail has no signature configured for your send-as addresses',
      imported.length > 0 ? 'success' : 'warning',
    );
    await refreshList();
  }, [importFromGmail, refreshList, toast]);

  return (
    <Card noHover>
      <CardHeader>
        <div className={styles.Header}>
          <span className={styles.HeaderIcon} aria-hidden="true">
            <PenLine />
          </span>
          <div className={styles.HeaderText}>
            <CardTitle>Email signatures</CardTitle>
            <CardDescription>
              Added automatically to email you send from Revvly. Bind a signature to a from-address
              and it is used whenever you send from that address, with the default covering
              everything else.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className={styles.Content}>
        {error ? (
          <Alert variant="destructive">
            <AlertDescription>
              Failed to load your signatures.{' '}
              <Button variant="outline" size="sm" onClick={() => void refetch()}>
                Try again
              </Button>
            </AlertDescription>
          </Alert>
        ) : loading && !data ? (
          <p className={styles.Muted}>Loading…</p>
        ) : signatures.length === 0 ? (
          <p className={styles.Muted}>
            No signatures yet. Email you send goes out without a sign-off.
          </p>
        ) : (
          <ul className={styles.List}>
            {signatures.map((signature) => (
              <li key={signature.id} className={styles.Row}>
                <div className={styles.RowHead}>
                  <span className={styles.Name}>{signature.name}</span>
                  {signature.isDefault && (
                    <Badge badgeType="outline" indicator="green">
                      Default
                    </Badge>
                  )}
                  {signature.sendingAddress && (
                    <Badge badgeType="outline" indicator="blue">
                      {signature.sendingAddress}
                    </Badge>
                  )}
                  {signature.source === 'GMAIL' && <Badge badgeType="outline">From Gmail</Badge>}
                </div>
                <div
                  className={classNames(styles.Preview)}
                  // Server-sanitized on save with the outbound-mail allowlist.
                  dangerouslySetInnerHTML={{ __html: signature.bodyHtml }}
                />
                <div className={styles.RowActions}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setDraft({
                        id: signature.id,
                        name: signature.name,
                        sendingAddress: signature.sendingAddress ?? '',
                        bodyHtml: signature.bodyHtml,
                      })
                    }
                  >
                    Edit
                  </Button>
                  {!signature.isDefault && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => void handleMakeDefault(signature)}
                    >
                      Make default
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPendingDelete(signature)}
                    disabled={deleting}
                  >
                    Delete
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}

        <div className={styles.Actions}>
          <Button variant="primary" onClick={() => setDraft({ ...EMPTY_DRAFT })}>
            New signature
          </Button>
          {gmailConnected && (
            <Button variant="outline" onClick={() => void handleImport()} disabled={importing}>
              {importing ? 'Importing…' : 'Import from Gmail'}
            </Button>
          )}
        </div>
        {crmEnabled && !gmailConnected && (
          <p className={styles.Muted}>
            Connect Gmail above to import the signature you already use there.
          </p>
        )}
      </CardContent>

      <Modal isOpen={draft !== null} onClose={() => setDraft(null)} className={styles.EditorModal}>
        <ModalHeader>
          <ModalTitle>{draft?.id ? 'Edit signature' : 'New signature'}</ModalTitle>
        </ModalHeader>
        <ModalBody>
          {draft && (
            <div className={styles.Form}>
              {formError && (
                <Alert variant="destructive">
                  <AlertDescription>{formError}</AlertDescription>
                </Alert>
              )}
              <div className={styles.Field}>
                <Label htmlFor="signature-name">Name</Label>
                <Input
                  id="signature-name"
                  value={draft.name}
                  onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                  placeholder="Full signature"
                  autoFocus
                />
              </div>
              <div className={styles.Field}>
                <Label htmlFor="signature-address">Use for this from-address (optional)</Label>
                <Input
                  id="signature-address"
                  type="email"
                  value={draft.sendingAddress}
                  onChange={(event) => setDraft({ ...draft, sendingAddress: event.target.value })}
                  placeholder="jane@yourcompany.com"
                />
                <p className={styles.Hint}>
                  Leave blank for a signature that applies to any address you send from.
                </p>
              </div>
              <div className={styles.Field}>
                <Label htmlFor="signature-body">Signature</Label>
                <SignatureEditor
                  value={draft.bodyHtml}
                  onChange={(bodyHtml) => setDraft({ ...draft, bodyHtml })}
                />
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setDraft(null)} disabled={creating || updating}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleSave()}
            disabled={creating || updating}
          >
            {creating || updating ? 'Saving…' : 'Save signature'}
          </Button>
        </ModalFooter>
      </Modal>

      {pendingDelete && (
        <ConfirmationModal onClose={() => setPendingDelete(null)}>
          <ConfirmationModal.Body>
            <p>
              Delete the signature &ldquo;{pendingDelete.name}&rdquo;? Email already sent keeps the
              signature it went out with.
            </p>
          </ConfirmationModal.Body>
          <ConfirmationModal.Actions>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPendingDelete(null)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              variant="danger"
              onClick={() => void handleDelete()}
              disabled={deleting}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          </ConfirmationModal.Actions>
        </ConfirmationModal>
      )}
    </Card>
  );
}
